import type { Address } from "./parse";
import { load, type Reading, shardFor, useShard } from "./shards";
import index from "./tables/delegation-index.json";

// What the five RIRs record as having been delegated, out of the statistics they publish daily and the NRO combines.
// It is a separate publication from the whois database and on separate terms — freely downloadable with a liability
// disclaimer and no redistribution clause — which is why this is here and the holder's name is not: the statistics
// carry an opaque hash where a name would be, and the name itself may not be republished by anybody.

// One delegation as the registries record it. The country is where the resource was registered rather than where it
// is used, which is a distinction the page has to make in words because nothing in the data does.
export interface Delegation {
  rir: string;
  country: string;
  date: string;
  // The block or the run of AS numbers this delegation actually covers, which is nearly always wider than what was
  // asked about and is the thing worth showing beside the answer.
  first: bigint;
  last: bigint;
}

// The same reading the hook does, as a plain call: a test stubs the fetch and holds the generator's writing and the
// page's reading against each other, which is the bargain tests/phone-number.test.ts makes with its maps.
export function delegationOf({ family, value }: Address): Promise<Delegation | undefined> {
  return family === "ipv4" ? readRanges("v4", value) : readPrefixes(value);
}

export function asDelegationOf(number: number): Promise<Delegation | undefined> {
  return readRanges("asn", BigInt(number));
}

export function useDelegation(address: Address | undefined): Reading<Delegation> {
  return useShard(
    address && `${address.family}:${address.value}`,
    (asked) => {
      const [family, value] = asked.split(":");
      return family === "ipv4" ? readRanges("v4", BigInt(value)) : readPrefixes(BigInt(value));
    },
  );
}

export function useAsDelegation(number: number | undefined): Reading<Delegation> {
  return useShard(number === undefined ? undefined : String(number), (asked) => readRanges("asn", BigInt(asked)));
}

// IPv4 and the AS numbers are both runs of consecutive values written as a start and a length, so one reading answers
// for both — the AS registry being a number line like any other once the addresses stop being addresses.
async function readRanges(name: "v4" | "asn", value: bigint): Promise<Delegation | undefined> {
  const shard = shardFor(INDEX[name], value);
  if (shard < 0) return undefined;

  const rows = await load<(number | string)[]>(url(name, shard));
  if (!rows) return undefined;

  let start = 0n;
  for (let at = 0; at < rows.length; at += 5) {
    const step = BigInt(rows[at] as string);
    start = at === 0 ? step : start + step;
    const end = start + BigInt(rows[at + 1] as string);
    // Sorted, so a row starting past the value means every row after it does too and nothing here holds it.
    if (value < start) return undefined;
    if (value <= end) return held(start, end, rows[at + 2] as number, rows[at + 3] as number, rows[at + 4] as string);
  }
  return undefined;
}

// IPv6 is delegated as prefixes and recorded as prefixes, so the width is the row's rather than a length to add on.
async function readPrefixes(value: bigint): Promise<Delegation | undefined> {
  const shard = shardFor(INDEX.v6, value);
  if (shard < 0) return undefined;

  const rows = await load<(number | string)[]>(url("v6", shard));
  if (!rows) return undefined;

  for (let at = 0; at < rows.length; at += 5) {
    const start = BigInt(`0x${rows[at] as string}`);
    const end = start + (1n << BigInt(128 - (rows[at + 1] as number))) - 1n;
    if (value < start) return undefined;
    if (value <= end) return held(start, end, rows[at + 2] as number, rows[at + 3] as number, rows[at + 4] as string);
  }
  return undefined;
}

function held(first: bigint, last: bigint, rir: number, country: number, date: string): Delegation {
  return {
    rir: INDEX.rirs[rir] ?? "",
    country: INDEX.countries[country] ?? "",
    // The statistics write a date as eight digits and a resource nobody dated as eight zeros, which is not a date.
    date: /^\d{8}$/.test(date) && date !== "00000000"
      ? `${date.slice(0, 4)}-${date.slice(4, 6)}-${date.slice(6)}`
      : "",
    first,
    last,
  };
}

// `?url` is what keeps 13 MB out of this chunk: each shard is emitted as an asset of its own, hashed by the build
// like every other, and read only when somebody asks about an address inside it.
const FILES = { "./delegations/asn-0.json": __glob_0, "./delegations/asn-1.json": __glob_1, "./delegations/asn-10.json": __glob_2, "./delegations/asn-11.json": __glob_3, "./delegations/asn-12.json": __glob_4, "./delegations/asn-13.json": __glob_5, "./delegations/asn-14.json": __glob_6, "./delegations/asn-15.json": __glob_7, "./delegations/asn-16.json": __glob_8, "./delegations/asn-17.json": __glob_9, "./delegations/asn-18.json": __glob_10, "./delegations/asn-19.json": __glob_11, "./delegations/asn-2.json": __glob_12, "./delegations/asn-20.json": __glob_13, "./delegations/asn-21.json": __glob_14, "./delegations/asn-22.json": __glob_15, "./delegations/asn-23.json": __glob_16, "./delegations/asn-24.json": __glob_17, "./delegations/asn-25.json": __glob_18, "./delegations/asn-26.json": __glob_19, "./delegations/asn-27.json": __glob_20, "./delegations/asn-28.json": __glob_21, "./delegations/asn-29.json": __glob_22, "./delegations/asn-3.json": __glob_23, "./delegations/asn-30.json": __glob_24, "./delegations/asn-31.json": __glob_25, "./delegations/asn-32.json": __glob_26, "./delegations/asn-33.json": __glob_27, "./delegations/asn-34.json": __glob_28, "./delegations/asn-35.json": __glob_29, "./delegations/asn-36.json": __glob_30, "./delegations/asn-37.json": __glob_31, "./delegations/asn-38.json": __glob_32, "./delegations/asn-39.json": __glob_33, "./delegations/asn-4.json": __glob_34, "./delegations/asn-40.json": __glob_35, "./delegations/asn-41.json": __glob_36, "./delegations/asn-42.json": __glob_37, "./delegations/asn-43.json": __glob_38, "./delegations/asn-44.json": __glob_39, "./delegations/asn-45.json": __glob_40, "./delegations/asn-46.json": __glob_41, "./delegations/asn-47.json": __glob_42, "./delegations/asn-48.json": __glob_43, "./delegations/asn-49.json": __glob_44, "./delegations/asn-5.json": __glob_45, "./delegations/asn-6.json": __glob_46, "./delegations/asn-7.json": __glob_47, "./delegations/asn-8.json": __glob_48, "./delegations/asn-9.json": __glob_49, "./delegations/v4-0.json": __glob_50, "./delegations/v4-1.json": __glob_51, "./delegations/v4-10.json": __glob_52, "./delegations/v4-100.json": __glob_53, "./delegations/v4-101.json": __glob_54, "./delegations/v4-102.json": __glob_55, "./delegations/v4-103.json": __glob_56, "./delegations/v4-104.json": __glob_57, "./delegations/v4-105.json": __glob_58, "./delegations/v4-106.json": __glob_59, "./delegations/v4-107.json": __glob_60, "./delegations/v4-108.json": __glob_61, "./delegations/v4-109.json": __glob_62, "./delegations/v4-11.json": __glob_63, "./delegations/v4-110.json": __glob_64, "./delegations/v4-111.json": __glob_65, "./delegations/v4-112.json": __glob_66, "./delegations/v4-113.json": __glob_67, "./delegations/v4-114.json": __glob_68, "./delegations/v4-12.json": __glob_69, "./delegations/v4-13.json": __glob_70, "./delegations/v4-14.json": __glob_71, "./delegations/v4-15.json": __glob_72, "./delegations/v4-16.json": __glob_73, "./delegations/v4-17.json": __glob_74, "./delegations/v4-18.json": __glob_75, "./delegations/v4-19.json": __glob_76, "./delegations/v4-2.json": __glob_77, "./delegations/v4-20.json": __glob_78, "./delegations/v4-21.json": __glob_79, "./delegations/v4-22.json": __glob_80, "./delegations/v4-23.json": __glob_81, "./delegations/v4-24.json": __glob_82, "./delegations/v4-25.json": __glob_83, "./delegations/v4-26.json": __glob_84, "./delegations/v4-27.json": __glob_85, "./delegations/v4-28.json": __glob_86, "./delegations/v4-29.json": __glob_87, "./delegations/v4-3.json": __glob_88, "./delegations/v4-30.json": __glob_89, "./delegations/v4-31.json": __glob_90, "./delegations/v4-32.json": __glob_91, "./delegations/v4-33.json": __glob_92, "./delegations/v4-34.json": __glob_93, "./delegations/v4-35.json": __glob_94, "./delegations/v4-36.json": __glob_95, "./delegations/v4-37.json": __glob_96, "./delegations/v4-38.json": __glob_97, "./delegations/v4-39.json": __glob_98, "./delegations/v4-4.json": __glob_99, "./delegations/v4-40.json": __glob_100, "./delegations/v4-41.json": __glob_101, "./delegations/v4-42.json": __glob_102, "./delegations/v4-43.json": __glob_103, "./delegations/v4-44.json": __glob_104, "./delegations/v4-45.json": __glob_105, "./delegations/v4-46.json": __glob_106, "./delegations/v4-47.json": __glob_107, "./delegations/v4-48.json": __glob_108, "./delegations/v4-49.json": __glob_109, "./delegations/v4-5.json": __glob_110, "./delegations/v4-50.json": __glob_111, "./delegations/v4-51.json": __glob_112, "./delegations/v4-52.json": __glob_113, "./delegations/v4-53.json": __glob_114, "./delegations/v4-54.json": __glob_115, "./delegations/v4-55.json": __glob_116, "./delegations/v4-56.json": __glob_117, "./delegations/v4-57.json": __glob_118, "./delegations/v4-58.json": __glob_119, "./delegations/v4-59.json": __glob_120, "./delegations/v4-6.json": __glob_121, "./delegations/v4-60.json": __glob_122, "./delegations/v4-61.json": __glob_123, "./delegations/v4-62.json": __glob_124, "./delegations/v4-63.json": __glob_125, "./delegations/v4-64.json": __glob_126, "./delegations/v4-65.json": __glob_127, "./delegations/v4-66.json": __glob_128, "./delegations/v4-67.json": __glob_129, "./delegations/v4-68.json": __glob_130, "./delegations/v4-69.json": __glob_131, "./delegations/v4-7.json": __glob_132, "./delegations/v4-70.json": __glob_133, "./delegations/v4-71.json": __glob_134, "./delegations/v4-72.json": __glob_135, "./delegations/v4-73.json": __glob_136, "./delegations/v4-74.json": __glob_137, "./delegations/v4-75.json": __glob_138, "./delegations/v4-76.json": __glob_139, "./delegations/v4-77.json": __glob_140, "./delegations/v4-78.json": __glob_141, "./delegations/v4-79.json": __glob_142, "./delegations/v4-8.json": __glob_143, "./delegations/v4-80.json": __glob_144, "./delegations/v4-81.json": __glob_145, "./delegations/v4-82.json": __glob_146, "./delegations/v4-83.json": __glob_147, "./delegations/v4-84.json": __glob_148, "./delegations/v4-85.json": __glob_149, "./delegations/v4-86.json": __glob_150, "./delegations/v4-87.json": __glob_151, "./delegations/v4-88.json": __glob_152, "./delegations/v4-89.json": __glob_153, "./delegations/v4-9.json": __glob_154, "./delegations/v4-90.json": __glob_155, "./delegations/v4-91.json": __glob_156, "./delegations/v4-92.json": __glob_157, "./delegations/v4-93.json": __glob_158, "./delegations/v4-94.json": __glob_159, "./delegations/v4-95.json": __glob_160, "./delegations/v4-96.json": __glob_161, "./delegations/v4-97.json": __glob_162, "./delegations/v4-98.json": __glob_163, "./delegations/v4-99.json": __glob_164, "./delegations/v6-0.json": __glob_165, "./delegations/v6-1.json": __glob_166, "./delegations/v6-10.json": __glob_167, "./delegations/v6-11.json": __glob_168, "./delegations/v6-12.json": __glob_169, "./delegations/v6-13.json": __glob_170, "./delegations/v6-14.json": __glob_171, "./delegations/v6-15.json": __glob_172, "./delegations/v6-16.json": __glob_173, "./delegations/v6-17.json": __glob_174, "./delegations/v6-18.json": __glob_175, "./delegations/v6-19.json": __glob_176, "./delegations/v6-2.json": __glob_177, "./delegations/v6-20.json": __glob_178, "./delegations/v6-21.json": __glob_179, "./delegations/v6-22.json": __glob_180, "./delegations/v6-23.json": __glob_181, "./delegations/v6-24.json": __glob_182, "./delegations/v6-25.json": __glob_183, "./delegations/v6-26.json": __glob_184, "./delegations/v6-27.json": __glob_185, "./delegations/v6-28.json": __glob_186, "./delegations/v6-29.json": __glob_187, "./delegations/v6-3.json": __glob_188, "./delegations/v6-30.json": __glob_189, "./delegations/v6-31.json": __glob_190, "./delegations/v6-32.json": __glob_191, "./delegations/v6-33.json": __glob_192, "./delegations/v6-34.json": __glob_193, "./delegations/v6-4.json": __glob_194, "./delegations/v6-5.json": __glob_195, "./delegations/v6-6.json": __glob_196, "./delegations/v6-7.json": __glob_197, "./delegations/v6-8.json": __glob_198, "./delegations/v6-9.json": __glob_199 } as Record<
  string,
  string
>;

function url(name: string, shard: number): string | undefined {
  return FILES[`./delegations/${name}-${shard}.json`];
}

const INDEX = index as { rirs: string[]; countries: string[]; v4: string[]; v6: string[]; asn: string[] };

// Two ASCII letters as the regional indicators a browser draws a flag from. The registries record a country as its
// alpha-2 code and nothing else, so this is arithmetic rather than a list — the country names would be the whole of
// world-countries dragged into a chunk that has no other use for it.
export function flagOf(country: string): string {
  if (!/^[A-Z]{2}$/.test(country)) return "";
  return [...country].map((letter) => String.fromCodePoint(0x1f1e6 + letter.charCodeAt(0) - 65)).join("");
}

import __glob_0 from "./delegations/asn-0.json?url";
import __glob_1 from "./delegations/asn-1.json?url";
import __glob_2 from "./delegations/asn-10.json?url";
import __glob_3 from "./delegations/asn-11.json?url";
import __glob_4 from "./delegations/asn-12.json?url";
import __glob_5 from "./delegations/asn-13.json?url";
import __glob_6 from "./delegations/asn-14.json?url";
import __glob_7 from "./delegations/asn-15.json?url";
import __glob_8 from "./delegations/asn-16.json?url";
import __glob_9 from "./delegations/asn-17.json?url";
import __glob_10 from "./delegations/asn-18.json?url";
import __glob_11 from "./delegations/asn-19.json?url";
import __glob_12 from "./delegations/asn-2.json?url";
import __glob_13 from "./delegations/asn-20.json?url";
import __glob_14 from "./delegations/asn-21.json?url";
import __glob_15 from "./delegations/asn-22.json?url";
import __glob_16 from "./delegations/asn-23.json?url";
import __glob_17 from "./delegations/asn-24.json?url";
import __glob_18 from "./delegations/asn-25.json?url";
import __glob_19 from "./delegations/asn-26.json?url";
import __glob_20 from "./delegations/asn-27.json?url";
import __glob_21 from "./delegations/asn-28.json?url";
import __glob_22 from "./delegations/asn-29.json?url";
import __glob_23 from "./delegations/asn-3.json?url";
import __glob_24 from "./delegations/asn-30.json?url";
import __glob_25 from "./delegations/asn-31.json?url";
import __glob_26 from "./delegations/asn-32.json?url";
import __glob_27 from "./delegations/asn-33.json?url";
import __glob_28 from "./delegations/asn-34.json?url";
import __glob_29 from "./delegations/asn-35.json?url";
import __glob_30 from "./delegations/asn-36.json?url";
import __glob_31 from "./delegations/asn-37.json?url";
import __glob_32 from "./delegations/asn-38.json?url";
import __glob_33 from "./delegations/asn-39.json?url";
import __glob_34 from "./delegations/asn-4.json?url";
import __glob_35 from "./delegations/asn-40.json?url";
import __glob_36 from "./delegations/asn-41.json?url";
import __glob_37 from "./delegations/asn-42.json?url";
import __glob_38 from "./delegations/asn-43.json?url";
import __glob_39 from "./delegations/asn-44.json?url";
import __glob_40 from "./delegations/asn-45.json?url";
import __glob_41 from "./delegations/asn-46.json?url";
import __glob_42 from "./delegations/asn-47.json?url";
import __glob_43 from "./delegations/asn-48.json?url";
import __glob_44 from "./delegations/asn-49.json?url";
import __glob_45 from "./delegations/asn-5.json?url";
import __glob_46 from "./delegations/asn-6.json?url";
import __glob_47 from "./delegations/asn-7.json?url";
import __glob_48 from "./delegations/asn-8.json?url";
import __glob_49 from "./delegations/asn-9.json?url";
import __glob_50 from "./delegations/v4-0.json?url";
import __glob_51 from "./delegations/v4-1.json?url";
import __glob_52 from "./delegations/v4-10.json?url";
import __glob_53 from "./delegations/v4-100.json?url";
import __glob_54 from "./delegations/v4-101.json?url";
import __glob_55 from "./delegations/v4-102.json?url";
import __glob_56 from "./delegations/v4-103.json?url";
import __glob_57 from "./delegations/v4-104.json?url";
import __glob_58 from "./delegations/v4-105.json?url";
import __glob_59 from "./delegations/v4-106.json?url";
import __glob_60 from "./delegations/v4-107.json?url";
import __glob_61 from "./delegations/v4-108.json?url";
import __glob_62 from "./delegations/v4-109.json?url";
import __glob_63 from "./delegations/v4-11.json?url";
import __glob_64 from "./delegations/v4-110.json?url";
import __glob_65 from "./delegations/v4-111.json?url";
import __glob_66 from "./delegations/v4-112.json?url";
import __glob_67 from "./delegations/v4-113.json?url";
import __glob_68 from "./delegations/v4-114.json?url";
import __glob_69 from "./delegations/v4-12.json?url";
import __glob_70 from "./delegations/v4-13.json?url";
import __glob_71 from "./delegations/v4-14.json?url";
import __glob_72 from "./delegations/v4-15.json?url";
import __glob_73 from "./delegations/v4-16.json?url";
import __glob_74 from "./delegations/v4-17.json?url";
import __glob_75 from "./delegations/v4-18.json?url";
import __glob_76 from "./delegations/v4-19.json?url";
import __glob_77 from "./delegations/v4-2.json?url";
import __glob_78 from "./delegations/v4-20.json?url";
import __glob_79 from "./delegations/v4-21.json?url";
import __glob_80 from "./delegations/v4-22.json?url";
import __glob_81 from "./delegations/v4-23.json?url";
import __glob_82 from "./delegations/v4-24.json?url";
import __glob_83 from "./delegations/v4-25.json?url";
import __glob_84 from "./delegations/v4-26.json?url";
import __glob_85 from "./delegations/v4-27.json?url";
import __glob_86 from "./delegations/v4-28.json?url";
import __glob_87 from "./delegations/v4-29.json?url";
import __glob_88 from "./delegations/v4-3.json?url";
import __glob_89 from "./delegations/v4-30.json?url";
import __glob_90 from "./delegations/v4-31.json?url";
import __glob_91 from "./delegations/v4-32.json?url";
import __glob_92 from "./delegations/v4-33.json?url";
import __glob_93 from "./delegations/v4-34.json?url";
import __glob_94 from "./delegations/v4-35.json?url";
import __glob_95 from "./delegations/v4-36.json?url";
import __glob_96 from "./delegations/v4-37.json?url";
import __glob_97 from "./delegations/v4-38.json?url";
import __glob_98 from "./delegations/v4-39.json?url";
import __glob_99 from "./delegations/v4-4.json?url";
import __glob_100 from "./delegations/v4-40.json?url";
import __glob_101 from "./delegations/v4-41.json?url";
import __glob_102 from "./delegations/v4-42.json?url";
import __glob_103 from "./delegations/v4-43.json?url";
import __glob_104 from "./delegations/v4-44.json?url";
import __glob_105 from "./delegations/v4-45.json?url";
import __glob_106 from "./delegations/v4-46.json?url";
import __glob_107 from "./delegations/v4-47.json?url";
import __glob_108 from "./delegations/v4-48.json?url";
import __glob_109 from "./delegations/v4-49.json?url";
import __glob_110 from "./delegations/v4-5.json?url";
import __glob_111 from "./delegations/v4-50.json?url";
import __glob_112 from "./delegations/v4-51.json?url";
import __glob_113 from "./delegations/v4-52.json?url";
import __glob_114 from "./delegations/v4-53.json?url";
import __glob_115 from "./delegations/v4-54.json?url";
import __glob_116 from "./delegations/v4-55.json?url";
import __glob_117 from "./delegations/v4-56.json?url";
import __glob_118 from "./delegations/v4-57.json?url";
import __glob_119 from "./delegations/v4-58.json?url";
import __glob_120 from "./delegations/v4-59.json?url";
import __glob_121 from "./delegations/v4-6.json?url";
import __glob_122 from "./delegations/v4-60.json?url";
import __glob_123 from "./delegations/v4-61.json?url";
import __glob_124 from "./delegations/v4-62.json?url";
import __glob_125 from "./delegations/v4-63.json?url";
import __glob_126 from "./delegations/v4-64.json?url";
import __glob_127 from "./delegations/v4-65.json?url";
import __glob_128 from "./delegations/v4-66.json?url";
import __glob_129 from "./delegations/v4-67.json?url";
import __glob_130 from "./delegations/v4-68.json?url";
import __glob_131 from "./delegations/v4-69.json?url";
import __glob_132 from "./delegations/v4-7.json?url";
import __glob_133 from "./delegations/v4-70.json?url";
import __glob_134 from "./delegations/v4-71.json?url";
import __glob_135 from "./delegations/v4-72.json?url";
import __glob_136 from "./delegations/v4-73.json?url";
import __glob_137 from "./delegations/v4-74.json?url";
import __glob_138 from "./delegations/v4-75.json?url";
import __glob_139 from "./delegations/v4-76.json?url";
import __glob_140 from "./delegations/v4-77.json?url";
import __glob_141 from "./delegations/v4-78.json?url";
import __glob_142 from "./delegations/v4-79.json?url";
import __glob_143 from "./delegations/v4-8.json?url";
import __glob_144 from "./delegations/v4-80.json?url";
import __glob_145 from "./delegations/v4-81.json?url";
import __glob_146 from "./delegations/v4-82.json?url";
import __glob_147 from "./delegations/v4-83.json?url";
import __glob_148 from "./delegations/v4-84.json?url";
import __glob_149 from "./delegations/v4-85.json?url";
import __glob_150 from "./delegations/v4-86.json?url";
import __glob_151 from "./delegations/v4-87.json?url";
import __glob_152 from "./delegations/v4-88.json?url";
import __glob_153 from "./delegations/v4-89.json?url";
import __glob_154 from "./delegations/v4-9.json?url";
import __glob_155 from "./delegations/v4-90.json?url";
import __glob_156 from "./delegations/v4-91.json?url";
import __glob_157 from "./delegations/v4-92.json?url";
import __glob_158 from "./delegations/v4-93.json?url";
import __glob_159 from "./delegations/v4-94.json?url";
import __glob_160 from "./delegations/v4-95.json?url";
import __glob_161 from "./delegations/v4-96.json?url";
import __glob_162 from "./delegations/v4-97.json?url";
import __glob_163 from "./delegations/v4-98.json?url";
import __glob_164 from "./delegations/v4-99.json?url";
import __glob_165 from "./delegations/v6-0.json?url";
import __glob_166 from "./delegations/v6-1.json?url";
import __glob_167 from "./delegations/v6-10.json?url";
import __glob_168 from "./delegations/v6-11.json?url";
import __glob_169 from "./delegations/v6-12.json?url";
import __glob_170 from "./delegations/v6-13.json?url";
import __glob_171 from "./delegations/v6-14.json?url";
import __glob_172 from "./delegations/v6-15.json?url";
import __glob_173 from "./delegations/v6-16.json?url";
import __glob_174 from "./delegations/v6-17.json?url";
import __glob_175 from "./delegations/v6-18.json?url";
import __glob_176 from "./delegations/v6-19.json?url";
import __glob_177 from "./delegations/v6-2.json?url";
import __glob_178 from "./delegations/v6-20.json?url";
import __glob_179 from "./delegations/v6-21.json?url";
import __glob_180 from "./delegations/v6-22.json?url";
import __glob_181 from "./delegations/v6-23.json?url";
import __glob_182 from "./delegations/v6-24.json?url";
import __glob_183 from "./delegations/v6-25.json?url";
import __glob_184 from "./delegations/v6-26.json?url";
import __glob_185 from "./delegations/v6-27.json?url";
import __glob_186 from "./delegations/v6-28.json?url";
import __glob_187 from "./delegations/v6-29.json?url";
import __glob_188 from "./delegations/v6-3.json?url";
import __glob_189 from "./delegations/v6-30.json?url";
import __glob_190 from "./delegations/v6-31.json?url";
import __glob_191 from "./delegations/v6-32.json?url";
import __glob_192 from "./delegations/v6-33.json?url";
import __glob_193 from "./delegations/v6-34.json?url";
import __glob_194 from "./delegations/v6-4.json?url";
import __glob_195 from "./delegations/v6-5.json?url";
import __glob_196 from "./delegations/v6-6.json?url";
import __glob_197 from "./delegations/v6-7.json?url";
import __glob_198 from "./delegations/v6-8.json?url";
import __glob_199 from "./delegations/v6-9.json?url";
